<template>
  <div>
    <div class="metrics-writing-skills">
      <div class="metrics-writing-skills__header">
        <span class="metrics-writing-skills__title heading-s">{{
          $t("metrics.writingSkillsTrajectory")
        }}</span>
        <eg-icon
          icon="information"
          class="metrics-writing-skills__info-icon"
          @click.native="
            $eventBus.$emit('onboarding:clicked:metricsWritingSkillsHint')
          "
        />
      </div>
      <p class="metrics-writing-skills__subtitle paragraph-s">
        {{ $t("metrics.recurringPatterns") }}
      </p>

      <infinity-loading v-if="isLoading" />

      <template v-else-if="hasData">
        <div class="metrics-writing-skills__list">
          <div
            v-for="(item, index) in skills"
            :key="index"
            class="metrics-writing-skills__item"
          >
            <div
              class="metrics-writing-skills__icon-wrap"
              :style="{ background: config(item.status).iconBg }"
            >
              <eg-icon
                :icon="config(item.status).icon"
                class="metrics-writing-skills__icon"
                :style="{ color: config(item.status).color }"
              />
            </div>

            <div class="metrics-writing-skills__content">
              <div class="metrics-writing-skills__meta paragraph-s">
                <span
                  class="metrics-writing-skills__trajectory"
                  :style="{ color: config(item.status).color }"
                  >{{ config(item.status).label }}</span
                >
                <span class="metrics-writing-skills__skill">{{
                  item.skillName
                }}</span>
              </div>
              <p class="metrics-writing-skills__description paragraph-s">
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <metrics-empty-state v-else-if="!isLoading" />
    </div>
  </div>
</template>

<script>
import MetricsEmptyState from "./metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";

const TRAJECTORY_CONFIG = {
  declining: {
    labelKey: "metrics.declining",
    icon: "arrow-down",
    color: "var(--color-text-semantic-onError, #DC3545)",
    iconBg: "#FDEFEC",
  },
  inconsistent: {
    labelKey: "metrics.inconsistent",
    icon: "flag",
    color: "var(--color-text-semantic-onWarning, #D76812)",
    iconBg: "#FFF6EB",
  },
  improving: {
    labelKey: "metrics.improving",
    icon: "check-circle",
    color: "var(--color-text-semantic-onSuccess, #308C4A)",
    iconBg: "#F0FAF3",
  },
  stable: {
    labelKey: "metrics.stable",
    icon: "arrow-right",
    color: "var(--color-text-neutral-secondary, #566370)",
    iconBg: "var(--color-surface-neutral-container-1, #F6F8FA)",
  },
  struggling: {
    labelKey: "metrics.struggling",
    icon: "risk-warning-error-danger",
    color: "var(--color-text-semantic-onError, #DC3545)",
    iconBg: "#FDEFEC",
  },
};

export default {
  name: "MetricsWritingSkillsTrajectory",

  components: { MetricsEmptyState, InfinityLoading },

  props: {
    insights: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    skills() {
      return this.insights?.summaryTrajectory?.skillsTrajectory || [];
    },
    hasData() {
      return this.skills.length > 0;
    },
  },

  methods: {
    config(status) {
      const key = (status || "").toLowerCase();
      const cfg = TRAJECTORY_CONFIG[key] || TRAJECTORY_CONFIG.stable;
      return { ...cfg, label: this.$t(cfg.labelKey) };
    },
  },
};
</script>

<style lang="scss" scoped>
.metrics-writing-skills {
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin: 0 24px;
  background: var(--color-surface-neutral-background, #fff);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  border-radius: 12px;
  gap: 16px;

  &__header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 0;
  }

  &__title {
    font-size: var(--typography-font-size-fs-600);
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__info-icon {
    color: var(--color-icon-neutral-secondary, #8a97a4);
    font-size: 14px;
    cursor: help;
  }

  &__subtitle {
    margin: -12px 0 0;
    color: var(--color-text-neutral-secondary, #566370);
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 0;

    &:not(:last-child) {
      border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    }

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }
  }

  &__icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__icon {
    font-size: 16px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__skill,
  &__trajectory {
    font-weight: var(--typography-font-weight-fw-600, 600);
  }

  &__skill {
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__description {
    color: var(--color-text-neutral-secondary, #566370);
    margin: 0;
  }
}
</style>
